import { brokerOrgJsonLd } from "@/lib/seo-schema";
import { PageDatesBadge } from "@/components/site/PageDatesBadge";
import { CountyInsightsPanel } from "@/components/site/CountyInsightsPanel";
import { LocalMarketNews } from "@/components/site/LocalMarketNews";
import { getCountyInsights } from "@/lib/county-insights.functions";
import { getState as getStateForCounty } from "@/data/states";
import { absoluteUrl } from "@/lib/site-origin";
import { fitTitle, fitDescription } from "@/lib/seo-head";
import { ExpertInsight } from "@/components/site/SeoBlocks";
import { buildCityExpertInsight } from "@/data/expert-insights";
import { states as ALL_STATES } from "@/data/states";
import { ProductReportCtaBanner } from "@/components/site/ProductReportCtaBanner";
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { SiteShell, PageHero } from "@/components/site/SiteShell";
import { QuickAnswer, PageOutro } from "@/components/site/PageEssentials";
import { Section, SectionHeader, FAQ, Breadcrumbs, CTABand, TableOfContents, StatRow, LoanGuidelines } from "@/components/site/Blocks";
import { DataSources } from "@/components/site/DataSources";
import { MarketUpdateSection } from "@/components/site/MarketUpdateSection";
import { RentalIncomeCompare } from "@/components/site/RentalIncomeCompare";
import { InvestmentRationale } from "@/components/site/InvestmentRationale";
import { LandlordLawPanel } from "@/components/site/LandlordLawPanel";
import { DscrToolkit } from "@/components/site/DscrToolkit";
import { CityGuideDownload } from "@/components/site/CityGuideDownload";
import { getProductHero } from "@/data/productHeroes";
import { getCityHero } from "@/data/city-heroes";
import { getState } from "@/data/states";
import { getProduct } from "@/data/products";
import { findCity, publishedInvestorCities, type InvestorCity } from "@/data/investor-cities";
import { getMarketStats, type MarketStats } from "@/lib/market-stats.functions";
import {
  localBusinessJsonLd,
  cityArticleJsonLd,
  faqJsonLd,
  breadcrumbListJsonLd,
  serviceJsonLd,
} from "@/lib/seo-schema";

import { cityKeywordsMeta } from "@/lib/page-keywords";
import { makeReportInlineCTA } from "@/components/site/ReportCta";

const InlineCTA = makeReportInlineCTA("foreign");


const SLUG = "foreign-national-loans";
const PRODUCT = getProduct(SLUG)!;

function fmtCurrency(n: number | null | undefined): string | null {
  if (n == null) return null;
  return `$${Intl.NumberFormat("en-US").format(Math.round(n))}`;
}
function fmtPopulation(n: number | null | undefined): string | null {
  if (n == null) return null;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${Math.round(n / 1_000)}K`;
  return Intl.NumberFormat("en-US").format(n);
}
function withLiveStats(city: InvestorCity, stats: MarketStats | null): InvestorCity {
  if (!stats) return city;
  return {
    ...city,
    medianHomeValue: fmtCurrency(stats.medianHomeValue) ?? city.medianHomeValue,
    population: fmtPopulation(stats.population) ?? city.population,
  };
}

export const Route = createFileRoute("/foreign-national-loans/$state/$city")({
  loader: async ({ params }) => {
    const rawCity = findCity(params.state, params.city);
    if (!rawCity) throw notFound();
    const countyInsights = await getCountyInsights({
      data: {
        stateAbbrev: getStateForCounty(params.state)?.abbrev ?? "",
        countyName: rawCity.county,
        placeLabel: `${rawCity.name}, ${rawCity.stateName}`,
      },
    }).catch(() => null);
    const stats = await getMarketStats({ data: { stateSlug: params.state, citySlug: params.city } }).catch(() => null);
    return {
      countyInsights,
      marketStats: stats,
      city: withLiveStats(rawCity, stats),
      liveVintage: stats?.vintage ?? null,
      liveMedianRent2BR: stats?.medianRent2BR ?? null,
      liveMedianRent3BR: stats?.medianRent3BR ?? null,
      liveUnemploymentRate: stats?.unemploymentRate ?? null,
      liveUnemploymentPeriod: stats?.unemploymentPeriod ?? null,
    };
  },
  head: ({ loaderData, params }) => {
    if (!loaderData) return { meta: [{ title: "City not found" }, { name: "robots", content: "noindex" }] };
    const c = loaderData.city;
    const title = fitTitle(`Foreign National Loans in ${c.name}, ${c.stateName} | No SSN, No US Credit`);
    const desc = `Foreign national mortgage in ${c.name}, ${c.stateName}: passport ID, no SSN, no US credit, up to 75% LTV (80% select). DSCR-qualified, close remotely. NMLS #2620881.`.slice(0, 158);
    const url = `/foreign-national-loans/${params.state}/${params.city}`;
    const faqs = cityFaqs(c.name, c.stateName);
    const hero = getCityHero(params.state, params.city, c.name, c.stateName);
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { name: "keywords", content: cityKeywordsMeta("foreign-national-loans", c.name, c.stateName) },
        { name: "robots", content: "index, follow, max-image-preview:large" },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:type", content: "article" },
        { property: "og:url", content: `https://dscrloanmarket.com${url}` },
        ...(hero ? [{ property: "og:image", content: absoluteUrl(hero.src) }, { property: "og:image:alt", content: hero.alt }] : []),
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: desc },
        ...(hero ? [{ name: "twitter:image", content: absoluteUrl(hero.src) }] : []),
        { property: "article:published_time", content: "2026-01-01" },
      ],
      links: [
        { rel: "canonical", href: `https://dscrloanmarket.com${url}` },
        { rel: "alternate", hrefLang: "en-us", href: `https://dscrloanmarket.com${url}` },
        { rel: "alternate", hrefLang: "x-default", href: `https://dscrloanmarket.com${url}` },
        ...(hero ? [{ rel: "preload", as: "image", href: hero.src }] : []),
      ],
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(faqJsonLd(faqs)) },
        { type: "application/ld+json", children: JSON.stringify(serviceJsonLd({ url, serviceName: `Foreign National Loans in ${c.name}, ${c.stateName}`, description: desc, state: c.stateName, city: c.name })) },
        { type: "application/ld+json", children: JSON.stringify(fnCityWebPageJsonLd({ url, city: c.name, state: c.stateName, description: desc })) },
        { type: "application/ld+json", children: JSON.stringify(fnCityLoanProductJsonLd({ url, city: c.name, state: c.stateName, description: desc })) },
        { type: "application/ld+json", children: JSON.stringify(fnCityHowToJsonLd({ url, city: c.name, state: c.stateName })) },
        { type: "application/ld+json", children: JSON.stringify(fnCityDatasetJsonLd({ url, city: c.name, state: c.stateName, zip: c.centroidZip })) },
        { type: "application/ld+json", children: JSON.stringify(fnCitySpeakableJsonLd({ url })) },
        { type: "application/ld+json", children: JSON.stringify(localBusinessJsonLd({ url, state: c.stateName, city: c.name })) },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            cityArticleJsonLd({
              url,
              headline: `Foreign National Loans in ${c.name}, ${c.stateName}`,
              description: desc,
              image: hero?.src,
            }),
          ),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            breadcrumbListJsonLd([
              { name: "Home", url: "/" },
              { name: "Foreign National Loans", url: `/${SLUG}` },
              { name: c.stateName, url: `/${SLUG}/${params.state}` },
              { name: c.name, url },
            ]),
          ),
        },
      ],
    };
  },
  component: CityPage,
  notFoundComponent: () => (
    <SiteShell hideBreadcrumbs>
      <Section>
        <div className="mx-auto max-w-2xl py-16 text-center">
          <h1 className="font-display text-3xl font-bold">City not found</h1>
          <p className="mt-2 text-muted-foreground">Try our full <Link to="/where-we-lend" className="text-brand underline">list of states where we lend</Link>.</p>
        </div>
      </Section>
    </SiteShell>
  ),
});

function fnCityWebPageJsonLd(opts: { url: string; city: string; state: string; description: string }) {
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": `${opts.url}#webpage`,
    url: opts.url,
    name: `Foreign National Loans in ${opts.city}, ${opts.state}`,
    description: opts.description,
    inLanguage: "en-US",
    isPartOf: { "@type": "WebSite", name: "Simply Approved Mortgages", url: "/" },
    about: { "@type": "LoanOrCredit", name: `Foreign national rental property loan in ${opts.city}, ${opts.state}` },
    mainEntity: { "@id": `${opts.url}#loan-product` },
  };
}

function fnCityLoanProductJsonLd(opts: { url: string; city: string; state: string; description: string }) {
  return {
    "@context": "https://schema.org",
    "@type": "FinancialProduct",
    "@id": `${opts.url}#loan-product`,
    name: `Foreign National Loans in ${opts.city}, ${opts.state}`,
    url: opts.url,
    description: opts.description,
    category: "Business-purpose foreign national rental mortgage",
    provider: brokerOrgJsonLd(),
    areaServed: [
      { "@type": "City", name: opts.city, containedInPlace: { "@type": "State", name: opts.state } },
      { "@type": "State", name: opts.state },
    ],
    feesAndCommissionsSpecification: "Loan amounts generally $150,000–$1,500,000; up to 75% LTV on purchase and rate/term, 70% on cash-out; no SSN or US credit required; DSCR minimum varies by program, reserves, and property type.",
  };
}

function fnCityHowToJsonLd(opts: { url: string; city: string; state: string }) {
  return {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name: `How foreign nationals finance a rental in ${opts.city}, ${opts.state}`,
    description: `Steps to close a business-purpose foreign national rental property loan in ${opts.city}, ${opts.state}.`,
    url: opts.url,
    step: [
      { "@type": "HowToStep", name: "Send passport + scenario", text: "Provide passport ID, country of residence, subject address, purchase price or payoff, and estimated rent." },
      { "@type": "HowToStep", name: "Form a US LLC", text: "Vest the property in a Delaware, Wyoming, or in-state LLC with a member/manager personal guarantee." },
      { "@type": "HowToStep", name: "Season reserves", text: "Hold 6–12 months of PITIA in a US-based or FATF-compliant bank for at least 60 days before close." },
      { "@type": "HowToStep", name: "Close remotely", text: "Sign via US consulate, embassy notary, or apostille — no US travel required to fund the loan." },
    ],
  };
}

function fnCityDatasetJsonLd(opts: { url: string; city: string; state: string; zip: string }) {
  return {
    "@context": "https://schema.org",
    "@type": "Dataset",
    name: `HUD FMR and rental market data for ${opts.city}, ${opts.state} foreign national investors`,
    description: `ZIP-level HUD Fair Market Rent lookup and rental income context used on the ${opts.city}, ${opts.state} foreign national loan guide.`,
    url: `${opts.url}#snapshot`,
    spatialCoverage: {
      "@type": "Place",
      name: `${opts.city}, ${opts.state}`,
      address: { "@type": "PostalAddress", postalCode: opts.zip, addressCountry: "US" },
    },
    temporalCoverage: "2026",
    creator: { "@type": "Organization", name: "Simply Approved Mortgages" },
    isBasedOn: "https://www.huduser.gov/portal/dataset/fmr-api.html",
  };
}

function fnCitySpeakableJsonLd(opts: { url: string }) {
  return {
    "@context": "https://schema.org",
    "@type": "SpeakableSpecification",
    cssSelector: ["h1", "#tldr", "#snapshot", "#program"],
    url: opts.url,
  };
}

function cityFaqs(city: string, state: string) {
  return [
    { q: `Do you lend to foreign nationals buying investment property in ${city}, ${state}?`, a: `Yes. We fund business-purpose foreign national loans on 1–4 unit residential, condos, and small multi-family properties in ${city} and throughout ${state}. No SSN or US credit required.` },
    { q: `What documents do foreign nationals need to buy in ${city}?`, a: `Valid unexpired passport, visa or proof of legal status (where applicable), two international credit references or a foreign credit report, 6–12 months of PITIA reserves seasoned 60 days in a US or FATF-compliant bank, and a US LLC for vesting in most states.` },
    { q: `What is the max LTV for a ${city} foreign national loan?`, a: `Up to 75% LTV standard on purchases, with 80% available in select cases based on reserves, credit references, DSCR, and property type. Cash-out refis typically cap at 65–70%.` },
    { q: `Do I need to be in the US to close on a ${city} property?`, a: `No. We coordinate remote closings via a US consulate, embassy notary, or approved apostille flow. Most foreign national ${city} closings happen without the borrower traveling.` },
    { q: `Can I close in a US LLC in ${city}?`, a: `Yes — and in most states we require it. We'll help you form a Delaware, Wyoming, or ${state} LLC and vest the ${city} property in the entity with a member/manager personal guarantee.` },
    { q: `Do you require US credit for a ${city} foreign national loan?`, a: `No. We qualify off DSCR (rent divided by PITIA) plus international credit references or a foreign credit bureau report. No US tax returns, W-2s, or SSN needed.` },
    { q: `How much do I need down to buy in ${city} as a foreign national?`, a: `Plan on 25–35% down (65–75% LTV standard), 5–7% closing costs, plus 6–12 months of PITIA reserves. On a typical ${city} price point that's meaningful capital — use our cash-to-close tool to size it.` },
    { q: `Can rental income from ${city} be used to qualify?`, a: `Yes. DSCR is calculated using the lower of the executed lease or the appraiser's Form 1007 market rent, divided by PITIA. Short-term rental income may be used with 12 months of history where ${city} rules allow.` },
    { q: `Are short-term rentals (Airbnb/VRBO) allowed for foreign nationals in ${city}?`, a: `Where ${city} zoning and HOA rules permit. Foreign national STR deals need proof the property is legally permitted plus supporting income data.` },
    { q: `How long does a foreign national closing take in ${city}?`, a: `Typical timeline is 21–30 business days once entity docs, passport, reserves proof, and appraisal are in. Clean files with a pre-formed LLC can close on the faster end.` },
    { q: `Are there prepayment penalties on foreign national loans in ${city}?`, a: `Most foreign national DSCR programs carry a step-down prepay (commonly 5/4/3/2/1 or 3-year) with buy-down options. We'll quote with and without so you can compare.` },
    { q: `Can I cash-out refinance a ${city} rental I already own?`, a: `Yes, typically up to 65–70% LTV on foreign national cash-out refis, subject to seasoning, DSCR, and reserves. Common way to recycle equity into the next ${city} deal.` },
    { q: `What countries can't you lend to?`, a: `We can't lend to residents of comprehensively OFAC-sanctioned jurisdictions (Cuba, Iran, North Korea, Syria). Sectoral-sanctions countries (Russia, Belarus, Venezuela, Myanmar) route to enhanced underwriter review.` },
    { q: `Do I need a US bank account to close on a ${city} property?`, a: `Yes. Reserves and the down payment must sit in a US-based or FATF-compliant bank seasoned 60 days prior to close. We can refer bank partners that open accounts for foreign nationals remotely.` },
    { q: `How are property taxes and insurance handled on ${city} foreign national deals?`, a: `We underwrite to post-closing tax reality (not the seller's bill) plus real insurance quotes — wind, flood, and HOA where applicable. This is the #1 reason ${city} DSCRs come in lower than expected.` },
  ];
}

function CityPage() {
  const { city, liveVintage, liveMedianRent2BR, liveMedianRent3BR, liveUnemploymentRate, liveUnemploymentPeriod, countyInsights, marketStats } = Route.useLoaderData();
  void getState(city.stateSlug);
  const stateCities = publishedInvestorCities.filter((c) => c.stateSlug === city.stateSlug && c.slug !== city.slug);
  const otherCities = stateCities.slice(0, 8);
  const hero = getCityHero(city.stateSlug, city.slug, city.name, city.stateName) ?? getProductHero(SLUG);
  const faqs = cityFaqs(city.name, city.stateName);

  return (
    <SiteShell hideBreadcrumbs productLabel="Foreign National">
      <PageHero
        eyebrow={`${city.name}, ${city.stateName}`}
        title={`Foreign National Loans in ${city.name}, ${city.stateName}`}
        subtitle={`Passport ID, no SSN, no US credit. Up to 75% LTV (80% select cases). Close on your ${city.name} rental remotely from abroad through a US LLC.`}
        image={hero}
      />
      <Section>
        <Breadcrumbs items={[
          { name: "Home", href: "/" },
          { name: "Foreign National Loans", href: `/${SLUG}` },
          { name: city.stateName, href: `/${SLUG}/${city.stateSlug}` },
          { name: city.name },
        ]} />
      </Section>

      <QuickAnswer
        question={`Can I get an investment property loan in ${city.name}, ${city.stateName}?`}
        answer={`Yes — Simply Approved Mortgages arranges business-purpose financing on non-owner-occupied investment property in ${city.name}, ${city.stateName}, qualified on the property's rental cash flow rather than personal tax returns. Every figure on this page is an estimate and remains subject to a complete application, credit and property review, appraisal where required, and full underwriting.`}
      />
      <TableOfContents
        items={[
          { href: "#tldr", label: "TL;DR" },
          { href: "#snapshot", label: "Market snapshot" },
          { href: "#income", label: "Rental income" },
          { href: "#program", label: "Program terms" },
          { href: "#guidelines", label: "Loan guidelines" },
          { href: "#fundamentals", label: "Investment fundamentals" },
          { href: "#landlord", label: "Landlord law" },
          { href: "#docs", label: "Documents" },
          { href: "#tools", label: "Fit tools" },
          { href: "#local-links", label: `${city.stateName} links` },
          { href: "#faq", label: "FAQ" },
        ]}
      />

      <Section id="tldr">
        <div className="rounded-2xl border border-brand-gold/30 bg-brand-gold/5 p-6 md:p-8">
          <div className="mb-3 text-xs font-semibold uppercase tracking-widest text-brand">
            TL;DR — Foreign National in {city.name}
          </div>
          <ul className="grid gap-2 text-sm md:grid-cols-2">
            <li>• Passport-only ID — no SSN, no ITIN required</li>
            <li>• No US credit or US tax returns needed</li>
            <li>• Up to 75% LTV on purchase and rate/term; 70% on cash-out</li>
            <li>• DSCR-qualified using {city.name} rents vs. PITIA</li>
            <li>• US LLC vesting (Delaware, Wyoming, or {city.stateName})</li>
            <li>• Remote closing via consulate / embassy / apostille</li>
            <li>• 6–12 months PITIA reserves in US/FATF-compliant bank</li>
            <li>• 21–30 business day typical close</li>
          </ul>
        </div>
      </Section>

      <Section id="snapshot">
        <div className="grid gap-8 lg:grid-cols-3">
          <div className="lg:col-span-2 space-y-8">
            <StatRow items={[
              { label: "Population", value: city.population },
              { label: "Median home value", value: city.medianHomeValue },
              { label: "Median 2BR rent", value: liveMedianRent2BR ? `$${liveMedianRent2BR.toLocaleString()}/mo` : city.medianRent2BR },
              { label: liveMedianRent3BR ? "Median 3BR rent" : "Cap rate band", value: liveMedianRent3BR ? `$${liveMedianRent3BR.toLocaleString()}/mo` : city.capRateBand },
            ]} />
            {(liveVintage || liveUnemploymentRate != null) && (
              <p className="mt-2 text-xs text-muted-foreground">
                {liveVintage && (<>Population, median home value, and rent by bedroom live from U.S. Census ACS {liveVintage} (B25031). </>)}
                {liveUnemploymentRate != null && (<>Unemployment: <strong>{liveUnemploymentRate.toFixed(1)}%</strong>{liveUnemploymentPeriod ? ` (${liveUnemploymentPeriod})` : ""} — U.S. Bureau of Labor Statistics LAUS, updated monthly.</>)}
              </p>
            )}
            <div className="prose max-w-none">
              <h2 className="font-display text-2xl font-bold text-brand-dark">Market snapshot</h2>
              <p className="mt-3 text-muted-foreground">{city.narrative}</p>
              {city.county && <p className="mt-2 text-sm text-muted-foreground">County: {city.county} · Centroid ZIP: {city.centroidZip}</p>}
              <p className="mt-3 text-muted-foreground">
                Comparing options? See our <Link to="/foreign-national-loans" className="font-semibold text-brand underline underline-offset-2">foreign national program</Link> or the <Link to="/foreign-national-loans/$state" params={{ state: city.stateSlug }} className="font-semibold text-brand underline underline-offset-2">{city.stateName} foreign national guide</Link> for state-level rules.
              </p>
            </div>
            <div className="grid gap-6 md:grid-cols-2">
              <div className="rounded-xl border border-border bg-card p-5">
                <h3 className="font-semibold text-brand-dark">Strengths</h3>
                <ul className="mt-2 space-y-1 text-sm text-muted-foreground">
                  {city.strengths.map((s: string) => <li key={s}>• {s}</li>)}
                </ul>
              </div>
              <div className="rounded-xl border border-border bg-card p-5">
                <h3 className="font-semibold text-brand-dark">Watch-outs</h3>
                <ul className="mt-2 space-y-1 text-sm text-muted-foreground">
                  {city.watchouts.map((s: string) => <li key={s}>• {s}</li>)}
                </ul>
              </div>
            </div>
            <div className="grid gap-6 md:grid-cols-2">
              <div className="rounded-xl border border-border bg-card p-5">
                <h3 className="font-semibold text-brand-dark">What to have ready</h3>
                <ul className="mt-2 space-y-1 text-sm text-muted-foreground">
                  <li>• Passport photo page + visa if applicable</li>
                  <li>• Two international credit references or foreign bureau report</li>
                  <li>• 60-day reserves in US/FATF-compliant bank</li>
                  <li>• Source-of-funds trail for down payment</li>
                  <li>• US LLC docs (or we help you form one)</li>
                </ul>
              </div>
              <div className="rounded-xl border border-border bg-card p-5">
                <h3 className="font-semibold text-brand-dark">How {city.name} typically prices</h3>
                <ul className="mt-2 space-y-1 text-sm text-muted-foreground">
                  <li>• Purchase and rate/term LTV up to 75%; cash-out up to 70%</li>
                  <li>• Best pricing at DSCR 1.20+ with 30% down</li>
                  <li>• Cash-out refi typically capped at 65–70% LTV</li>
                  <li>• STR income allowed with 12-mo history where permitted</li>
                  <li>• 21–30 business days once entity + reserves are in</li>
                </ul>
              </div>
            </div>
          </div>
          <div className="space-y-6">
            <CityGuideDownload city={city} />
            <div id="lead-form" className="rounded-xl border border-border bg-card p-6 shadow-sm">
              <h3 className="font-display text-lg font-bold text-foreground">Get foreign national pricing for {city.name}</h3>
              <p className="mt-2 text-sm text-muted-foreground">A licensed loan officer replies with an initial scenario review once we have your credit authorization and supporting documents — usually same day. Formal pricing and terms follow underwriting.</p>
              <Link to="/apply" className="mt-4 inline-flex items-center justify-center rounded-md bg-brand-gold px-5 py-2.5 text-sm font-semibold text-primary hover:bg-brand-gold-hover">Start My Approval</Link>
              <Link to="/rates" className="mt-2 inline-flex items-center justify-center rounded-md border border-border bg-background px-5 py-2.5 text-sm font-semibold text-foreground hover:border-brand hover:text-brand">See Today's Rates</Link>
            </div>
            <div className="rounded-xl border border-border bg-card p-5 text-xs text-muted-foreground">
              <div className="font-semibold text-foreground">Reviewed by our cross-border desk — no US credit required, entity and OFAC handled up front</div>
              <div className="mt-1">Licensed loan officer team · NMLS #2620881</div>
              <PageDatesBadge only="reviewed" className="mt-2" />
            </div>
            <div className="rounded-xl border border-border bg-card p-5">
              <h3 className="font-semibold text-brand-dark">Other {stateCities.length > 0 ? city.stateName : "investor"} cities</h3>
              <ul className="mt-3 grid grid-cols-2 gap-2 text-sm">
                {otherCities.map((c) => (
                  <li key={`${c.stateSlug}-${c.slug}`}>
                    <Link to="/foreign-national-loans/$state/$city" params={{ state: c.stateSlug, city: c.slug }} className="text-brand hover:underline">
                      {c.name}, {c.stateName}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </Section>

      <Section id="income">
        <RentalIncomeCompare
          zip={city.centroidZip}
          locationLabel={`${city.name}, ${city.stateName}`}
          medianRent2BR={city.medianRent2BR}
          medianHomeValue={city.medianHomeValue}
          airbnbAdr={city.airbnbAdr}
          airbnbOccupancy={city.airbnbOccupancy}
        />
      </Section>

      <InlineCTA
        title={`Compare rental income on a ${city.name} address`}
        body="Use Section 8 FMR, single-family market rent, and short-term-rental assumptions before you make an offer — then send us the scenario and a licensed loan officer will follow up with indicative numbers, subject to underwriting."
        cta="Get Your Personalized Rate Quote"
        variant="soft"
      />

      <Section id="program">
        <SectionHeader eyebrow="Program terms" title={`Foreign national terms in ${city.name}`} />
        <div className="mt-6 grid gap-4 md:grid-cols-2">
          <Card title="Down payment & LTV" body="25–35% down (65–75% LTV standard). 80% LTV available in select cases with strong reserves, credit references, and DSCR." />
          <Card title="Reserves" body="6–12 months of PITIA in a US-based or FATF-compliant bank, seasoned 60 days prior to close." />
          <Card title="Vesting" body={`US LLC required in ${city.stateName} for foreign national investors. Delaware or Wyoming LLC accepted; we can refer formation partners.`} />
          <Card title="Credit" body="No US credit needed. Two international credit references or a foreign bureau report (Equifax Canada, Experian UK, etc.)." />
          <Card title="Rate structure" body="30-year fixed and 5/6, 7/6 ARM options. Step-down or 3-year prepay standard with buy-down available." />
          <Card title="Closing" body={`Remote close via US consulate, embassy notary, or apostille. No US travel required to fund a ${city.name} purchase.`} />
        </div>
      </Section>

      <Section id="guidelines">
        <LoanGuidelines
          productLabel={`Foreign National · ${city.name}, ${city.stateName}`}
          rows={[
            { label: "Loan amount", value: "$150k – $3M", why: "Sized to appraised value, program caps, and market comps." },
            { label: "Max LTV", value: "Up to 75% (80% select) / 65–70% cash-out", why: "Best pricing sits ~5% below the LTV cap; reserves and credit references drive the tier." },
            { label: "Min DSCR", value: "1.00 (best at 1.20+)", why: `In ${city.name}, current 2BR rent near ${city.medianRent2BR} typically supports 1.10–1.30 DSCR.` },
            { label: "Reserves", value: "6–12 months PITIA", why: "Held in a US-based or FATF-compliant bank, seasoned 60 days prior to close." },
            { label: "Credit", value: "No US credit required", why: "Two international references or foreign bureau report (Equifax Canada, Experian UK, etc.)." },
            { label: "Terms", value: "30-yr fixed, 5/6 & 7/6 ARM, I/O options", why: "Fixed or hybrid ARM picked to match hold period." },
            { label: "Vesting", value: "US LLC (Delaware / Wyoming / in-state)", why: "Business-purpose foreign national loans close in a US entity with member/manager guarantee." },
            { label: "Typical close", value: "21–30 business days", why: "Entity formation + apostille flow add a few days over domestic DSCR files." },
          ]}
          note="Ranges are typical program guidelines — not a commitment to lend. Final terms are established in an initial scenario review (formal pricing after underwriting) after underwriting review."
        />
      </Section>

      <Section id="fundamentals">
        <InvestmentRationale
          location={`${city.name}, ${city.stateName}`}
          economy={city.economy ?? `${city.name} sits inside a service-area investor market where foreign national files should be underwritten to verified rent, taxes, insurance, HOA dues, and local rental rules.`}
          employers={city.employers}
          transit={city.transit ?? `Review neighborhood access to employment centers, transit routes, and major roads before assuming long-term tenant demand in ${city.name}.`}
          livability={city.livability ?? `${city.name} deals should be checked at the neighborhood level for vacancy, insurance, taxes, HOA restrictions, STR rules, and landlord/tenant timelines.`}
          demandDrivers={city.demandDrivers?.length ? city.demandDrivers : city.strengths}
        />
        <div className="mt-6">
          <DataSources locationLabel={`${city.name}, ${city.stateName}`} />
        </div>
      </Section>

      <Section id="landlord">
        <LandlordLawPanel stateSlug={city.stateSlug} stateName={city.stateName} />
      </Section>

      <Section id="docs">
        <SectionHeader eyebrow="Documents" title={`What to gather before you apply for a ${city.name} foreign national loan`} />
        <ul className="mt-6 grid gap-2 md:grid-cols-2 text-sm text-foreground">
          <li>• Valid, unexpired passport (photo page)</li>
          <li>• Visa or proof of legal status (where applicable)</li>
          <li>• Two international credit references OR foreign credit report</li>
          <li>• 60-day reserves statements from US/FATF-compliant bank</li>
          <li>• Source-of-funds documentation for down payment</li>
          <li>• US LLC operating agreement (or we help you form one)</li>
          <li>• Signed purchase contract (or payoff statement for refi)</li>
          <li>• Executed lease (if tenant-occupied) or market rent projection</li>
        </ul>
      </Section>

      <MarketUpdateSection productSlug="foreign-national-loans" productLabel={`Foreign National · ${city.name}, ${city.stateName}`} />

      <Section>
        <ProductReportCtaBanner productKey="foreign" variant="full" locationLabel={`${city.name}, ${city.stateName}`} />
      </Section>

      <DscrToolkit
        context={`Foreign National investor loan — ${city.name}, ${city.stateName}`}
        title={`${city.name} foreign national fit tools`}
        intro={`Estimate DSCR on a ${city.name} rental, size the largest loan the property qualifies for, and package a scenario a licensed LO can price today.`}
      />

      <InlineCTA
        title={`Price a ${city.name} foreign national deal`}
        body="Submit a complete application with passport, entity plan, subject address, rent, and supporting docs — a licensed LO replies with an initial scenario review, usually same day. Formal pricing follows underwriting."
        cta="Get Your Personalized Rate Quote"
      />

      <Section id="local-links">
        <SectionHeader
          eyebrow="Keep exploring"
          title={`More ${city.stateName} investor resources`}
          intro={`Jump into the ${city.stateName} state guide, our foreign national program, current rates, and other ${city.stateName} markets we lend in.`}
        />
        <div className="mt-8 grid gap-5 lg:grid-cols-3">
          <Link to="/foreign-national-loans" className="group rounded-xl border border-border bg-card p-5 transition hover:border-brand">
            <div className="text-xs font-semibold uppercase tracking-wide text-brand-gold">Program hub</div>
            <h3 className="mt-2 font-display text-lg font-bold text-foreground group-hover:text-brand">Foreign national loan requirements →</h3>
            <p className="mt-2 text-sm text-muted-foreground">LTV, reserves, entity vesting, documents, and remote closing path.</p>
          </Link>
          <Link to="/foreign-national-loans/$state" params={{ state: city.stateSlug }} className="group rounded-xl border border-border bg-card p-5 transition hover:border-brand">
            <div className="text-xs font-semibold uppercase tracking-wide text-brand-gold">State guide</div>
            <h3 className="mt-2 font-display text-lg font-bold text-foreground group-hover:text-brand">{city.stateName} foreign national guide →</h3>
            <p className="mt-2 text-sm text-muted-foreground">State-level lending notes, top markets, landlord rules, and rental benchmarks.</p>
          </Link>
          <Link to="/rates" className="group rounded-xl border border-border bg-card p-5 transition hover:border-brand">
            <div className="text-xs font-semibold uppercase tracking-wide text-brand-gold">Pricing</div>
            <h3 className="mt-2 font-display text-lg font-bold text-foreground group-hover:text-brand">See Today's Rates →</h3>
            <p className="mt-2 text-sm text-muted-foreground">Review current rate context before sending the subject property for written pricing.</p>
          </Link>
        </div>
        {otherCities.length > 0 && (
          <div className="mt-8">
            <h3 className="font-display text-xl font-bold text-foreground">Top {city.stateName} markets foreign national investors compare with {city.name}</h3>
            <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {otherCities.map((c) => (
                <Link
                  key={`${c.stateSlug}-${c.slug}`}
                  to="/foreign-national-loans/$state/$city"
                  params={{ state: c.stateSlug, city: c.slug }}
                  className="rounded-lg border border-border bg-card px-4 py-3 text-sm transition hover:border-brand hover:text-brand"
                >
                  <span className="font-semibold">{c.name}</span>
                  <span className="block text-xs text-muted-foreground">{c.medianRent2BR} 2BR rent · {c.capRateBand} cap</span>
                </Link>
              ))}
            </div>
          </div>
        )}
      </Section>

      <Section id="faq">
        <SectionHeader
          eyebrow="FAQ"
          title={`${city.name} foreign national loan questions`}
          intro={`Answers to the most common non-US investor questions about foreign national loans in ${city.name}, ${city.stateName}.`}
          center
        />
        <div className="mt-8"><FAQ items={faqs} /></div>
      </Section>

      <ExpertInsight insight={buildCityExpertInsight(city.name, city.stateName, "foreign-national-loans", "foreign national loan", ALL_STATES.find((s) => s.name === city.stateName))} />

      <CTABand
        title={`Ready to finance a ${city.name} rental from abroad?`}
        body={`${PRODUCT.tagline} Passport, US LLC, DSCR — that's the file.`}
      />
      <LocalMarketNews
        stateName={city.stateName}
        stateAbbrev={getStateForCounty(city.stateSlug)?.abbrev ?? ""}
        cityName={city.name}
        countyName={city.county ?? null}
        stats={marketStats ?? null}
        statsScope="city"
        countyInsights={countyInsights ?? null}
      />
      <CountyInsightsPanel
        stateAbbrev={getStateForCounty(city.stateSlug)?.abbrev ?? ""}
        stateName={city.stateName}
        countyName={city.county}
        placeLabel={`${city.name}, ${city.stateName}`}
        initialData={countyInsights}
      />

      <PageOutro hideInsight />
    </SiteShell>
  );
}


function Stat({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="mt-1 font-display text-xl font-bold text-foreground">{value}</div>
      {sub && <div className="mt-0.5 text-[11px] text-muted-foreground">{sub}</div>}
    </div>
  );
}
function Card({ title, body }: { title: string; body: string }) {
  return (
    <div className="rounded-xl border border-border bg-card p-5">
      <div className="font-display text-base font-semibold text-foreground">{title}</div>
      <p className="mt-2 text-sm text-muted-foreground">{body}</p>
    </div>
  );
}
